<script lang="ts" setup>
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import ErrorMessage from './ErrorMessage.vue';
import { i18n } from '@/i18n';

const t = i18n.t;

const props = defineProps<{
  index: number;
  modelValue: string;
  options: string[];
  placeholder: string;
  label: string;
  disabled?: boolean;
  isError?: boolean;
  errorMessage?: string;
}>();

// const selected = ref<string>(props.modelValue);
const selected = computed({
  /**
   * 選択された値を取得します。
   * @returns - 選択された値
   */
  get: () => props.modelValue,
  /**
   * 選択された値を設定します。
   * @param value - 選択された値
   */
  set: (value: string) => {
    emit('update:modelValue', value);
  },
});

const isOpen = ref(false);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

watch(
  () => props.modelValue,
  () => {
    selected.value = props.modelValue;
  }
);

/**
 * placeholderを表示するかどうか
 */
const isPlaceholder = computed(() => {
  return selected.value === '' || selected.value === null || selected.value === undefined;
});

/**
 * ドロップダウンの表示状態を切り替えます。
 */
const toggleDropdown = () => {
  if (!props.disabled) {
    isOpen.value = !isOpen.value;
  }
};

/**
 * オプションを選択し、親コンポーネントに選択値を通知します。
 * @param option - 選択されたオプション
 */
const selectOption = (option: string) => {
  selected.value = option;
  emit('update:modelValue', option);
  isOpen.value = false;
};

/**
 * ドロップダウンを閉じます。
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!isOpen.value) return;
  const target = event.target;
  const selector = `#order-status-select-${props.index}`;
  const arrowSelector = `#order-status-select-arrow-${props.index}`;
  if (!(target instanceof Element)) return;
  if (target.closest(arrowSelector)) return;
  if (target.closest(selector)) return;

  isOpen.value = false;
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
  window.addEventListener('resize', calculateDropdownPosition);
  window.addEventListener('scroll', calculateDropdownPosition, true);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
  window.removeEventListener('resize', calculateDropdownPosition);
  window.removeEventListener('scroll', calculateDropdownPosition, true);
});

/**
 * ドロップダウンの位置を計算し、設定します。
 */
const calculateDropdownPosition = () => {
  nextTick(() => {
    const selectBox = document.querySelector(`#order-status-select-${props.index}`) as HTMLElement;
    if (selectBox) {
      const rect = selectBox.getBoundingClientRect();
      const dropdown = document.querySelector(`#order-status-select-dropdown-${props.index}`) as HTMLElement;
      if (dropdown) {
        dropdown.style.left = `${rect.left}px`; // スクロールを考慮した絶対位置
        dropdown.style.top = `${rect.bottom}px`; // スクロールを考慮した絶対位置
        dropdown.style.width = `${rect.width}px`; // select-boxの幅に合わせて設定

        // max-heightを計算して設定
        const availableHeight = window.innerHeight - rect.bottom - 10; // 10pxは少しのマージン
        dropdown.style.maxHeight = `${Math.max(availableHeight, 100)}px`; // 最小高さを100pxとする
        dropdown.style.overflowY = 'auto'; // スクロール可能にする
      }
    }
  });
};

watch(isOpen, (newVal) => {
  if (newVal) {
    calculateDropdownPosition();

    // ドロップダウンが開いたときに選択された要素をスクロールして表示する
    nextTick(() => {
      const dropdown = document.querySelector(`#order-status-select-dropdown-${props.index}`);
      const selectedEl = dropdown?.querySelector('.select-box__option--selected') as HTMLElement | null;

      if (dropdown && selectedEl) {
        const dropdownHeight = dropdown.clientHeight;
        const optionTop = selectedEl.offsetTop;

        // 表示枠の高さより選択肢の上が下すぎたら限界までスクロール、それ以外は上に来るように
        const scrollTop = Math.min(optionTop, dropdown.scrollHeight - dropdownHeight);
        dropdown.scrollTop = scrollTop;
      }
    });
  }
});
</script>

<template>
  <div class="select-box_container">
    <div v-if="label !== ''" class="select-box__label">
      <p class="select-box__label-text">{{ label }}</p>
      <ErrorMessage :is-error="isError" :error-message="errorMessage" />
    </div>
    <div :id="`order-status-select-${index}`" class="select-box">
      <div
        class="select-box__selected"
        :class="{ 'select-box__selected--disabled': disabled, 'select-box__selected--error': isError }"
        @click="toggleDropdown"
      >
        <div
          :class="`select-box__field ${isPlaceholder ? 'select-box__field--placeholder' : ''} ${
            disabled ? 'select-box__selected--disabled' : ''
          }`"
        >
          <!-- 選択肢した値を表示する。何も選択していない場合はplaceholderの文言を表示する -->
          <div>{{ isPlaceholder ? placeholder : selected }}</div>
        </div>
        <SvgLoader v-if="isOpen" name="arrowUp" class="select-box__arrow" />
        <SvgLoader v-else :id="`order-status-select-arrow-${index}`" name="arrowDown" class="select-box__arrow" />
      </div>
      <div v-if="isOpen" :id="`order-status-select-dropdown-${index}`" class="select-box__dropdown">
        <div class="select-box__option" @click="selectOption('')">
          {{ t('選択を外す') }}
        </div>
        <div
          v-for="option in options"
          :key="option"
          class="select-box__option"
          :class="{ 'select-box__option--selected': selected === option }"
          @click="selectOption(option)"
        >
          {{ option }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.select-box {
  justify-items: center;
  position: relative;
  user-select: none;
  box-sizing: border-box;

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 0;
  }

  &__label {
    display: grid;
    grid-template-columns: auto auto;
    gap: 3px;
    justify-content: space-between;
    align-items: center;

    &-text {
      padding: 8px 0;
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 18.2px;
      text-align: center;

      color: $textDefault;
    }
  }

  &__field {
    height: 20px;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;
    background-color: $semanticWhite;

    &--placeholder {
      color: $textSecondary;
    }
  }

  &__selected {
    width: 100%;
    color: white;
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr 24px;
    text-align: center;
    align-items: center;
    padding: 6px 8px;
    background-color: $semanticWhite;
    border: 1px solid transparent;
    border-bottom: 1px solid $borderDefault;

    &--disabled {
      background-color: $surfaceSecondary;
      cursor: not-allowed;
    }

    &--error {
      border: 1px solid $semanticError;
    }
  }

  &__arrow {
    display: grid;
    align-content: center;
    justify-content: center;
    height: 24px;
    width: 24px;
  }

  &__dropdown {
    position: fixed;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1050; // モーダルより高い値に設定
    overflow-y: auto;
    max-height: 300px; // 適切な最大高さを設定
    box-sizing: border-box;
  }

  &__option {
    padding: 10px;
    cursor: pointer;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;

    min-height: 40px;

    &:hover,
    &:focus {
      background-color: $semanticPrimarySubdued;
    }

    &--selected {
      background-color: $semanticPrimarySubdued02;
      font-weight: bold;
    }

    &--disabled {
      color: $surfaceSecondary;
      cursor: not-allowed;
    }
  }
}
</style>
